import React, { useEffect, useMemo, useState } from "react"
import {
  Container,
  Card,
  CardBody,
  Modal,
  Form,
  Label,
  Input,
  Row,
  Col,
  Badge,
} from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import Swal from "sweetalert2"
import axios from "axios"
import configure from "configure"
import Loader from "pages/Separate/Loader"
import secureLocalStorage from "react-secure-storage"
import { fetchNodeData } from "utils/common-fetchData"
import { formatDate } from "utils/formatDate"
import "./ExEmployeeFeedback.css"

const getInitials = name => {
  if (!name) return "EX"
  const parts = String(name).trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

const avatarBg = name => {
  const colors = [
    "linear-gradient(135deg, #752a97, #a855c9)",
    "linear-gradient(135deg, #0ea5e9, #6366f1)",
    "linear-gradient(135deg, #059669, #34d399)",
    "linear-gradient(135deg, #ea580c, #f59e0b)",
    "linear-gradient(135deg, #dc2626, #f87171)",
    "linear-gradient(135deg, #4f46e5, #a855f7)",
  ]
  let hash = 0
  const str = String(name || "ex")
  for (let i = 0; i < str.length; i++) hash += str.charCodeAt(i)
  return colors[hash % colors.length]
}

// Local "ignore" store — declined feedback ids stay hidden even after refresh.
// Backend delete is attempted first; local filtering guarantees the UX
// ("will not be shown in the list afterwards") even if the API lacks support.
const IGNORED_KEY = "exfb_ignored_ids"

const getIgnoredIds = () => {
  try {
    const parsed = JSON.parse(localStorage.getItem(IGNORED_KEY) || "[]")
    return Array.isArray(parsed) ? parsed : []
  } catch (e) {
    return []
  }
}

// Table cells show a compact 1-line preview; full text opens in the detail modal.
// Threshold keeps short texts as-is (no "Read more" noise).
const isLongText = (text, limit = 100) => {
  if (!text) return false
  const str = String(text)
  return str.length > limit || str.split("\n").length > 1
}

const EmployeeAvatar = ({ row, size = 40 }) => {
  const name = row.name || "Ex Employee"
  if (row.avatar) {
    return (
      <img
        src={`${configure.imgUrl}${row.avatar}`}
        alt={name}
        title={name}
        className="exfb-avatar-img"
        style={{ width: size, height: size }}
        onError={e => {
          e.currentTarget.style.display = "none"
        }}
      />
    )
  }
  return (
    <div
      className="exfb-avatar-fallback"
      title={name}
      style={{ width: size, height: size, background: avatarBg(name) }}
    >
      {getInitials(name)}
    </div>
  )
}

const ExEmployeeFeedback = () => {
  const [loading, setLoading] = useState(false)
  const [feedbackList, setFeedbackList] = useState([])
  const [search, setSearch] = useState("")

  const [modal, setModal] = useState(false)
  const [editRow, setEditRow] = useState(null)
  const [managementAction, setManagementAction] = useState("")
  const [saving, setSaving] = useState(false)
  const [detailRow, setDetailRow] = useState(null)
  const [decliningId, setDecliningId] = useState(null)

  useEffect(() => {
    document.title = "Ex-Employee Feedback | Zithas HR"
    fetchFeedback()
  }, [])

  const fetchFeedback = async () => {
    const token = secureLocalStorage.getItem("auth_token")
    const res = await fetchNodeData(setLoading, "employeeFeedback/list", token)
    if (res?.success) {
      const ignored = getIgnoredIds()
      const list = res.data || []
      setFeedbackList(
        ignored.length
          ? list.filter(r => !ignored.includes(r.id))
          : list
      )
    }
  }

  const openActionModal = row => {
    setEditRow(row)
    setManagementAction(row.management_action || "")
    setModal(true)
  }

  const handleSaveAction = async e => {
    e.preventDefault()
    if (!editRow) return
    setSaving(true)
    try {
      const token = secureLocalStorage.getItem("auth_token")
      const res = await axios.put(
        `${configure.nodeApi}/employeeFeedback/${editRow.id}/action`,
        {
          management_action: managementAction,
          action_by:
            localStorage.getItem("auth_name") ||
            secureLocalStorage.getItem("auth_name") ||
            "Management",
        },
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      )
      if (res.data?.success) {
        Swal.fire({
          icon: "success",
          title: "Management action saved",
          timer: 1500,
          showConfirmButton: false,
        })
        setModal(false)
        setEditRow(null)
        setManagementAction("")
        fetchFeedback()
      } else {
        Swal.fire({
          icon: "error",
          title: "Save failed",
          text: res.data?.message || "Something went wrong",
          confirmButtonColor: "#752a97",
        })
      }
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Save failed",
        text: err?.response?.data?.message || err.message,
        confirmButtonColor: "#752a97",
      })
    }
    setSaving(false)
  }

  const handleDecline = async row => {
    if (!row || decliningId) return
    const result = await Swal.fire({
      title: "Ignore this feedback?",
      text: `Feedback from ${row.name || "this ex-employee"} will be hidden and will not be shown in the list afterwards.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, ignore it",
      cancelButtonText: "Cancel",
    })
    if (!result.isConfirmed) return
    setDecliningId(row.id)
    try {
      const token = secureLocalStorage.getItem("auth_token")
      try {
        await axios.delete(
          `${configure.nodeApi}/employeeFeedback/${row.id}`,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        )
      } catch (apiErr) {
        // Backend may not expose a delete endpoint yet — local ignore still hides it.
      }
      const ignored = getIgnoredIds()
      if (!ignored.includes(row.id)) {
        localStorage.setItem(
          IGNORED_KEY,
          JSON.stringify([...ignored, row.id])
        )
      }
      setFeedbackList(prev => prev.filter(r => r.id !== row.id))
      if (detailRow?.id === row.id) setDetailRow(null)
      Swal.fire({
        icon: "success",
        title: "Feedback ignored",
        text: "It will no longer appear in the list.",
        timer: 1500,
        showConfirmButton: false,
      })
    } finally {
      setDecliningId(null)
    }
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return feedbackList
    return feedbackList.filter(
      r =>
        (r.name || "").toLowerCase().includes(q) ||
        (r.email || "").toLowerCase().includes(q) ||
        (r.feedback || "").toLowerCase().includes(q) ||
        (r.management_action || "").toLowerCase().includes(q)
    )
  }, [feedbackList, search])

  const totalCount = feedbackList.length
  const actionedCount = feedbackList.filter(r => r.management_action).length
  const pendingCount = totalCount - actionedCount

  return (
    <React.Fragment>
      {loading && <Loader />}
      <div className="page-content mb-4 exfb-page">
        <Container fluid>
          <Breadcrumbs title="Dashboard" breadcrumbItem="Ex-Employee Feedback" />

          {/* Summary strip */}
          <Row className="g-3 mb-3">
            <Col md={4}>
              <div className="exfb-stat exfb-stat-total">
                <div className="exfb-stat-icon">
                  <i className="fas fa-comments"></i>
                </div>
                <div>
                  <div className="exfb-stat-num">{totalCount}</div>
                  <div className="exfb-stat-label">Total Feedback</div>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="exfb-stat exfb-stat-done">
                <div className="exfb-stat-icon">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div>
                  <div className="exfb-stat-num">{actionedCount}</div>
                  <div className="exfb-stat-label">Action Taken</div>
                </div>
              </div>
            </Col>
            <Col md={4}>
              <div className="exfb-stat exfb-stat-pending">
                <div className="exfb-stat-icon">
                  <i className="fas fa-hourglass-half"></i>
                </div>
                <div>
                  <div className="exfb-stat-num">{pendingCount}</div>
                  <div className="exfb-stat-label">Pending Review</div>
                </div>
              </div>
            </Col>
          </Row>

          <Card className="exfb-card">
            <CardBody>
              {/* Header + search — standard search-box pattern so icon aligns */}
              <Row className="align-items-center mb-3">
                <Col md={6} className="d-flex align-items-center gap-2">
                  <div className="exfb-title-icon">
                    <i className="fas fa-comments text-white"></i>
                  </div>
                  <h5 className="mb-0 fw-bold text-purple">
                    Ex-Employee Feedback
                  </h5>
                  <Badge color="light" className="text-dark border exfb-count">
                    {filtered.length} record{filtered.length === 1 ? "" : "s"}
                  </Badge>
                </Col>
                <Col
                  md={6}
                  className="d-flex justify-content-md-end mt-2 mt-md-0"
                >
                  <div className="search-box d-inline-block">
                    <div className="position-relative">
                      <input
                        type="text"
                        className="border-radius form-control rounded"
                        placeholder="Search name / email / feedback..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        style={{ minWidth: 260 }}
                      />
                      <i className="bx bx-search-alt search-icon"></i>
                    </div>
                  </div>
                </Col>
              </Row>

              {filtered.length === 0 && !loading ? (
                <div className="text-center text-muted py-5">
                  <i className="fas fa-inbox fa-3x mb-3 d-block opacity-50"></i>
                  <p className="mb-0">No ex-employee feedback found.</p>
                </div>
              ) : (
                <div className="table-responsive exfb-table-wrap">
                  <table className="table mb-0 exfb-table">
                    <thead>
                      <tr>
                        <th style={{ width: 46 }}>#</th>
                        <th style={{ minWidth: 220 }}>Employee</th>
                        <th style={{ minWidth: 260 }}>Feedback</th>
                        <th style={{ minWidth: 240 }}>
                          Action Taken by Management
                        </th>
                        <th style={{ width: 120 }}>Submitted On</th>
                        <th className="text-center" style={{ width: 150 }}>
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((row, i) => (
                        <tr key={row.id}>
                          <td className="text-muted">{i + 1}</td>
                          <td>
                            <div className="d-flex gap-2 align-items-center">
                              <EmployeeAvatar row={row} />
                              <div className="exfb-emp-meta">
                                <div className="fw-semibold exfb-emp-name">
                                  {row.name || "Ex Employee"}
                                </div>
                                <div
                                  className="text-muted exfb-emp-email"
                                  title={row.email}
                                >
                                  {row.email}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="exfb-quote exfb-clamp">
                              {row.feedback}
                            </div>
                            {isLongText(row.feedback) && (
                              <button
                                type="button"
                                className="exfb-readmore"
                                onClick={() => setDetailRow(row)}
                              >
                                Read more
                                <i className="fas fa-angle-right ms-1"></i>
                              </button>
                            )}
                          </td>
                          <td>
                            {row.management_action ? (
                              <div className="exfb-action-done">
                                <div className="exfb-action-text exfb-clamp">
                                  {row.management_action}
                                </div>
                                {isLongText(row.management_action) && (
                                  <button
                                    type="button"
                                    className="exfb-readmore"
                                    onClick={() => setDetailRow(row)}
                                  >
                                    Read more
                                    <i className="fas fa-angle-right ms-1"></i>
                                  </button>
                                )}
                                {(row.action_by || row.action_at) && (
                                  <div className="exfb-action-meta">
                                    {row.action_by
                                      ? `By ${row.action_by}`
                                      : ""}
                                    {row.action_by && row.action_at
                                      ? "  •  "
                                      : ""}
                                    {row.action_at
                                      ? formatDate(row.action_at)
                                      : ""}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="exfb-pending-badge">
                                <i className="fas fa-clock me-1"></i>Pending
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="exfb-date">
                              <i className="far fa-calendar-alt me-1"></i>
                              {formatDate(row.created_at)}
                            </span>
                          </td>
                          <td className="text-center">
                            <div className="d-flex gap-1 justify-content-center">
                              <button
                                className="btn btn-sm exfb-btn-view"
                                onClick={() => setDetailRow(row)}
                                title="View full feedback"
                              >
                                <i className="fas fa-eye"></i>
                              </button>
                              <button
                                className="btn btn-sm exfb-btn-edit"
                                onClick={() => openActionModal(row)}
                                title={
                                  row.management_action
                                    ? "Edit management action"
                                    : "Add management action"
                                }
                              >
                                <i className="fas fa-pencil-alt"></i>
                              </button>
                              <button
                                className="btn btn-sm exfb-btn-decline"
                                onClick={() => handleDecline(row)}
                                disabled={decliningId === row.id}
                                title="Decline / ignore this feedback"
                              >
                                <i className="fas fa-times"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Container>
      </div>

      <Modal isOpen={modal} centered toggle={() => setModal(false)}>
        <div className="modal-header px-4 py-3">
          <h5 className="modal-title mt-0" style={{ fontSize: 15 }}>
            <i className="fas fa-gavel me-2 text-purple"></i>
            Action Taken by Management
          </h5>
          <button
            type="button"
            onClick={() => setModal(false)}
            className="btn-modal-close"
          >
            <span className="btn-modal-span">&times;</span>
          </button>
        </div>
        <div className="modal-body px-4 py-3">
          {editRow && (
            <div className="exfb-modal-emp">
              <EmployeeAvatar row={editRow} size={36} />
              <div>
                <div className="fw-semibold" style={{ fontSize: 13 }}>
                  {editRow.name || "Ex Employee"}
                </div>
                <div className="text-muted" style={{ fontSize: 11 }}>
                  {editRow.email}
                </div>
              </div>
            </div>
          )}
          {editRow && (
            <div className="exfb-quote exfb-fullbox mb-3">
              {editRow.feedback}
            </div>
          )}
          <Form onSubmit={handleSaveAction}>
            <div className="mb-3">
              <Label className="mb-1" style={{ fontSize: 12 }}>
                Management Action{" "}
                <span
                  className="text-muted"
                  style={{ fontWeight: 400, fontSize: 11 }}
                >
                  (leave blank to clear)
                </span>
              </Label>
              <Input
                type="textarea"
                rows={4}
                placeholder="e.g. Discussed with team lead, process updated, employee contacted..."
                value={managementAction}
                onChange={e => setManagementAction(e.target.value)}
              />
            </div>
            <div className="mt-3 pt-3" style={{ borderTop: "1px solid #e9ecef" }}>
              <button
                type="submit"
                className="border-radius btn btn-purple btn-sm px-4"
                disabled={saving}
              >
                <i className="fas fa-save me-2"></i>
                {saving ? "Saving..." : "Save Action"}
              </button>
              <button
                type="button"
                className="border-radius btn btn-light btn-sm px-4 ms-2"
                onClick={() => setModal(false)}
              >
                Cancel
              </button>
            </div>
          </Form>
        </div>
      </Modal>

      {/* Full feedback + action detail modal — handles 20-line texts without breaking the table */}
      <Modal
        isOpen={!!detailRow}
        size="lg"
        centered
        toggle={() => setDetailRow(null)}
      >
        <div className="modal-header px-4 py-3">
          <h5 className="modal-title mt-0" style={{ fontSize: 15 }}>
            <i className="fas fa-comment-dots me-2 text-purple"></i>
            Feedback Detail
          </h5>
          <button
            type="button"
            onClick={() => setDetailRow(null)}
            className="btn-modal-close"
          >
            <span className="btn-modal-span">&times;</span>
          </button>
        </div>
        {detailRow && (
          <div className="modal-body px-4 py-3">
            <div className="exfb-modal-emp">
              <EmployeeAvatar row={detailRow} size={44} />
              <div className="flex-grow-1">
                <div className="fw-semibold" style={{ fontSize: 14 }}>
                  {detailRow.name || "Ex Employee"}
                </div>
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {detailRow.email} • Submitted{" "}
                  {formatDate(detailRow.created_at)}
                </div>
              </div>
              {detailRow.management_action ? (
                <span className="exfb-done-pill">
                  <i className="fas fa-check me-1"></i>Actioned
                </span>
              ) : (
                <span className="exfb-pending-badge">
                  <i className="fas fa-clock me-1"></i>Pending
                </span>
              )}
            </div>

            <Label
              className="mb-1 mt-1 text-uppercase fw-bold"
              style={{ fontSize: 11, letterSpacing: 0.4 }}
            >
              Employee Feedback
            </Label>
            <div className="exfb-quote exfb-fullbox mb-3">
              {detailRow.feedback}
            </div>

            <Label
              className="mb-1 text-uppercase fw-bold"
              style={{ fontSize: 11, letterSpacing: 0.4 }}
            >
              Action Taken by Management
            </Label>
            {detailRow.management_action ? (
              <div className="exfb-action-done mb-2">
                <div className="exfb-action-text exfb-fulltext">
                  {detailRow.management_action}
                </div>
                {(detailRow.action_by || detailRow.action_at) && (
                  <div className="exfb-action-meta">
                    {detailRow.action_by
                      ? `By ${detailRow.action_by}`
                      : ""}
                    {detailRow.action_by && detailRow.action_at
                      ? "  •  "
                      : ""}
                    {detailRow.action_at
                      ? formatDate(detailRow.action_at)
                      : ""}
                  </div>
                )}
              </div>
            ) : (
              <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                No action recorded yet.
              </div>
            )}

            <div className="mt-3 pt-3" style={{ borderTop: "1px solid #e9ecef" }}>
              <button
                type="button"
                className="border-radius btn btn-purple btn-sm px-4"
                onClick={() => {
                  const row = detailRow
                  setDetailRow(null)
                  openActionModal(row)
                }}
              >
                <i className="fas fa-pencil-alt me-2"></i>
                {detailRow.management_action ? "Edit Action" : "Add Action"}
              </button>
              <button
                type="button"
                className="border-radius btn btn-danger btn-sm px-4 ms-2"
                disabled={decliningId === detailRow.id}
                onClick={() => handleDecline(detailRow)}
              >
                <i className="fas fa-times me-2"></i>
                {decliningId === detailRow.id ? "Ignoring..." : "Decline"}
              </button>
              <button
                type="button"
                className="border-radius btn btn-light btn-sm px-4 ms-2"
                onClick={() => setDetailRow(null)}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </React.Fragment>
  )
}

export default ExEmployeeFeedback
